/**
 * Requirement management page — integrates tabs + list/overview + filter.
 */

import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Typography, Button, Space, message, Modal, List, Alert } from 'antd';
import { PlusOutlined, EyeOutlined, DownloadOutlined, NotificationOutlined, ExportOutlined, SyncOutlined } from '@ant-design/icons';
import FeishuExportModal from '@/components/requirements/FeishuExportModal';
import FeishuSyncModal from '@/components/requirements/FeishuSyncModal';
import { useRequirementStore } from '@/store/requirementStore';
import { useConfigStore } from '@/store/configStore';
import { useAuthStore } from '@/store/authStore';
import SubcategoryTabs from '@/components/requirements/SubcategoryTabs';
import RequirementList from '@/components/requirements/RequirementList';
import {
  resolveNotifyScope,
  canPruneNotifyScope,
  canUseSharedNotifyList,
  loadNotifyScope,
  saveNotifyScope,
  clearNotifyScope,
  reconcileNotifyList,
  buildNotifyRows,
  buildNotifyBatch,
  scopedNotifyKey,
  type NotifyEntry,
  type NotifyScopeSnapshot,
} from '@/components/requirements/notifyList';
import RequirementOverview, { type RequirementOverviewRef } from '@/components/requirements/RequirementOverview';
import RequirementFilter, {
  EMPTY_FILTERS,
  type RequirementFilters,
} from '@/components/requirements/RequirementFilter';
import RequirementCreateModal from '@/components/requirements/RequirementCreateModal';
import PublishVersionModal from '@/components/requirements/PublishVersionModal';
import PreviewChangesModal from '@/components/requirements/PreviewChangesModal';
import type { Requirement, RequirementOverviewResponse, Project } from '@/types';
import { useT } from '@/i18n';
import { listRequirements, sendRequirementChangeNotificationBatch } from '@/api/requirements';
import { getProject } from '@/api/config';
import { getNewRequirements } from '@/api/requirementFlowback';
import apiClient from '@/api/client';
import { exportRequirementsToExcel } from '@/utils/exportExcel';
import { CopyExternalLinkButton } from '@/components/packages/CopyExternalLinkButton';

const { Title } = Typography;

/** Text fields checked when matching the free-text "内容" keyword filter. */
const REQ_TEXT_FIELDS: (keyof Requirement)[] = [
  'reqId', 'description', 'owner', 'deliveryStage', 'deliverables',
  'verificationMethod', 'validationCriteria', 'status', 'category',
  'remark', 'bspTag', 'bspTag1', 'bspTag2', 'linkedPackageName',
];

/** Whether a requirement contains the given keyword in any of its text fields. */
const matchesKeyword = (req: Requirement, keyword: string): boolean => {
  const kw = keyword.trim().toLowerCase();
  if (!kw) return true;
  if (REQ_TEXT_FIELDS.some((f) => String(req[f] ?? '').toLowerCase().includes(kw))) return true;
  return (req.customerReqIds ?? []).some((id) => id.toLowerCase().includes(kw));
};

/** Whether a requirement matches the keyword and all active conditions. */
const matchesFilters = (req: Requirement, filters: RequirementFilters): boolean => {
  if (!matchesKeyword(req, filters.keyword)) return false;
  return filters.conditions.every(
    (c) => c.values.length === 0 || c.values.includes(String(req[c.field] ?? ''))
  );
};

/** Apply filters to a requirements array. */
const filterRequirements = (
  reqs: Requirement[],
  filters: RequirementFilters
): Requirement[] => reqs.filter((req) => matchesFilters(req, filters));

/** Apply filters to overview data using allRequirements for cross-reference. */
const filterOverview = (
  overview: RequirementOverviewResponse,
  filters: RequirementFilters,
  allRequirements: Requirement[]
): RequirementOverviewResponse => {
  const activeConditions = filters.conditions.filter((c) => c.values.length > 0);
  const hasFilters = filters.keyword.trim() !== '' || activeConditions.length > 0;
  if (!hasFilters) return overview;

  // Build a set of reqIds that pass all filters; falls back to status-only
  // matching when allRequirements hasn't loaded yet.
  let allowedReqIds: Set<string> | null = null;
  if (allRequirements.length > 0) {
    allowedReqIds = new Set(
      allRequirements.filter((r) => matchesFilters(r, filters)).map((r) => r.reqId)
    );
  }

  const statusConditions = activeConditions.filter((c) => c.field === 'status');

  const filtered = overview.statusDistribution
    .filter((item) => statusConditions.every((c) => c.values.includes(item.status)))
    .map((item) => {
      if (!allowedReqIds) return item;
      const ids = item.requirementIds.filter((id) => allowedReqIds!.has(id));
      return { ...item, count: ids.length, requirementIds: ids };
    })
    .filter((item) => item.count > 0);

  return {
    ...overview,
    totalCount: filtered.reduce((sum, i) => sum + i.count, 0),
    statusDistribution: filtered,
  };
};

/**
 * 山头作用域内一次拉取到的条目快照。
 * 每条都带自己的 projectId：不同车型出现同一 reqId 时是两条独立条目，不做去重。
 */
interface NotifyScopeData extends NotifyScopeSnapshot {
  entries: NotifyEntry<Requirement>[];
  /** 本次拉取实际请求的车型快照；与当前作用域不一致时说明集合已过期。 */
  projectIds: number[];
}

const RequirementManagement: React.FC = () => {
  const { t } = useT();
  const { projectId } = useParams<{ projectId: string }>();
  const numericProjectId = Number(projectId);
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const targetReqId = searchParams.get('reqId');

  const [activeTab, setActiveTab] = useState('overview');
  const resolvedReqIdRef = useRef<string | null>(null);

  const [filters, setFilters] = useState<RequirementFilters>(EMPTY_FILTERS);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [feishuExportOpen, setFeishuExportOpen] = useState(false);
  const [feishuSyncOpen, setFeishuSyncOpen] = useState(false);

  const { projects, mountains } = useConfigStore();
  const [fetchedProject, setFetchedProject] = useState<Project | null>(null);
  const {
    requirements,
    allRequirements,
    allRequirementsProjectId,
    overview,
    isLoading,
    error,
    fetchRequirements,
    fetchAllRequirements,
    fetchOverview,
    reset,
  } = useRequirementStore();

  const storeProject = projects.find((p) => p.id === numericProjectId);
  const project = storeProject ?? fetchedProject ?? undefined;

  // 主线项目不在 configStore 中，store 找不到时单独拉取
  useEffect(() => {
    if (!numericProjectId || storeProject) return;
    getProject(String(numericProjectId))
      .then(setFetchedProject)
      .catch(() => {});
  }, [numericProjectId, storeProject]);

  // Reset resolved marker when the target reqId changes (user clicked a different requirement).
  useEffect(() => {
    resolvedReqIdRef.current = null;
  }, [targetReqId]);

  // Derive active tab from URL reqId: look up subcategory from allRequirements.
  // Once resolved, skip re-runs to prevent infinite loop when reset() clears allRequirements.
  useEffect(() => {
    if (!targetReqId) return;
    if (resolvedReqIdRef.current === targetReqId) return;
    const found = allRequirements.find((r) => r.reqId === targetReqId);
    if (found?.subcategory) {
      resolvedReqIdRef.current = targetReqId;
      setActiveTab(found.subcategory);
    }
  }, [targetReqId, allRequirements]);

  // Get user for permission checks
  const { user } = useAuthStore();

  // 主线新增需求（subcategory 云朵）：拉取项目缺失的主线需求 + 同步权限判断
  const [newReqSubcats, setNewReqSubcats] = useState<Set<string>>(new Set());
  const [canSync, setCanSync] = useState(false);
  const [rootProjectName, setRootProjectName] = useState<string | null>(null);

  useEffect(() => {
    if (!numericProjectId) {
      setNewReqSubcats(new Set());
      setCanSync(false);
      setRootProjectName(null);
      return;
    }
    let cancelled = false;
    Promise.all([
      getProject(String(numericProjectId)),
      getNewRequirements(numericProjectId),
    ])
      .then(([proj, nr]) => {
        if (cancelled) return;
        const ok = !!user && (
          user.user_type === 'internal' ||
          user.username === proj.owner ||
          (proj.requirement_change_users ?? []).some((u) => u.id === user.id)
        );
        setCanSync(ok);
        setRootProjectName(nr.root_project_name);
        setNewReqSubcats(new Set(nr.items.map((i) => i.subcategory)));
      })
      .catch(() => {
        if (cancelled) return;
        setCanSync(false);
        setRootProjectName(null);
        setNewReqSubcats(new Set());
      });
    return () => { cancelled = true; };
  }, [numericProjectId, user]);

  const handleCloudClick = (subcat: string) => {
    const src = rootProjectName ?? 'mainLine';
    const des = project?.name ?? '';
    navigate(
      `/requirement-sync?srcProject=${encodeURIComponent(src)}&srcSubcatagory=${encodeURIComponent(subcat)}&desProject=${encodeURIComponent(des)}&desSubcatagory=${encodeURIComponent(subcat)}&newReqProjectId=${numericProjectId}`,
    );
  };

  // Ref to RequirementOverview component for refreshing versions
  const overviewRef = useRef<RequirementOverviewRef>(null);

  // Version button handlers (show modals, actual logic is in RequirementOverview)
  const [publishModalOpen, setPublishModalOpen] = useState(false);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);

  // 需求变更通知列表：同一山头下的所有车型共用一个列表；无山头项目回落到项目级。
  // 成员事实源是服务端山头成员表（mountains store 的 mountain.projects）；
  // projects 过滤结果只作并集补充 —— 并集只会多拉车型、绝不会少拉，
  // 因此最坏只会「少裁剪」，不会误删兄弟车型的条目。
  const mountainId = project?.mountain?.id ?? null;
  const scopeMountain = useMemo(
    () => (mountainId == null ? null : mountains.find((m) => m.id === mountainId) ?? null),
    [mountainId, mountains]
  );
  const siblingProjectIds = useMemo(() => {
    if (mountainId == null) return [];
    const ids = new Set<number>((scopeMountain?.projects ?? []).map((p) => p.id));
    for (const p of projects) {
      if (p.mountain?.id === mountainId) ids.add(p.id);
    }
    return [...ids];
  }, [mountainId, scopeMountain, projects]);
  // 山头成员表已加载 = 成员集合由服务端权威数据确认；未加载时禁止裁剪（安全降级）。
  const membersResolved = mountainId == null || scopeMountain != null;
  const notifyScope = useMemo(
    () =>
      resolveNotifyScope({
        projectId: numericProjectId,
        mountainId,
        siblingProjectIds,
        membersResolved,
      }),
    [numericProjectId, mountainId, siblingProjectIds, membersResolved]
  );
  const [notifyList, setNotifyList] = useState<Set<string>>(() =>
    loadNotifyScope(notifyScope, localStorage)
  );
  const [notifyScopeData, setNotifyScopeData] = useState<NotifyScopeData | null>(null);
  const [notifyScopeLoading, setNotifyScopeLoading] = useState(false);
  const notifyRequestSeq = useRef(0);
  const [notifyModalOpen, setNotifyModalOpen] = useState(false);
  const [notifySending, setNotifySending] = useState(false);
  const hiddenPasswordRef = useRef<HTMLInputElement>(null);

  const handlePublishClick = () => {
    setPublishModalOpen(true);
  };

  const handlePublishClose = () => {
    setPublishModalOpen(false);
    // Refresh version list after publishing
    overviewRef.current?.refreshVersions();
  };

  const handlePreviewClick = () => {
    setPreviewModalOpen(true);
  };

  const handlePreviewClose = () => {
    setPreviewModalOpen(false);
  };

  const handleExportClick = async () => {
    // 数据验证
    if (!allRequirements || allRequirements.length === 0) {
      message.warning(t('暂无需求数据可导出'));
      return;
    }

    // 执行导出
    setIsExporting(true);
    try {
      await exportRequirementsToExcel(allRequirements, project?.name || '未命名项目');
      message.success(t('导出成功！'));
    } catch (error) {
      console.error('导出失败:', error);
      message.error(t('导出失败，请重试'));
    } finally {
      setIsExporting(false);
    }
  };

  const isSharedRequirements = project?.req_mode === 'shared';

  // 只有 mainLine 池项目（名称为 'mainLine' 的主线项目）才能新建/编辑需求
  const isMainlinePool = project?.is_mainline === true && project?.name === 'mainLine';

  const canEditRequirements =
    user?.user_type === 'internal' && (
      user.username === project?.owner ||
      (project?.requirement_change_users ?? []).some((u) => u.id === user.id)
    );

  // 新建/编辑权限：非主线项目正常；主线项目只有 mainLine 池可操作
  const canCreateAndEdit = canEditRequirements && (!project?.is_mainline || isMainlinePool);

  // 其他主线项目列表（用于新建需求时选择同步目标）
  const [otherMainlineProjects, setOtherMainlineProjects] = useState<{ id: number; name: string }[]>([]);
  useEffect(() => {
    if (!isMainlinePool) return;
    apiClient.get<{ code: number; data: { id: number; project: string }[] }>('/requirements/mainline-projects/')
      .then((res) => {
        const list = (res.data?.data || [])
          .filter((p) => p.project !== 'mainLine')
          .map((p) => ({ id: p.id, name: p.project }));
        setOtherMainlineProjects(list);
      })
      .catch(() => {});
  }, [isMainlinePool]);

  useEffect(() => {
    reset();
    if (!numericProjectId) return;
    if (isSharedRequirements) return;

    if (activeTab === 'design-documents') {
      // Design documents are fetched directly by DesignDocumentTable component
      return;
    }

    if (activeTab === 'overview') {
      fetchOverview(numericProjectId);
      fetchAllRequirements(numericProjectId);
    } else {
      fetchRequirements(numericProjectId, activeTab);
      fetchAllRequirements(numericProjectId);
    }
  }, [numericProjectId, activeTab, isSharedRequirements]);

  /**
   * 拉取山头内所有车型的需求，构建 (projectId, reqId) 条目快照。
   * 任一车型拉取失败（403/超时）即 complete=false：此时并集不完整，调用方不得裁剪。
   */
  const refreshNotifyScope = useCallback(async () => {
    // 快照本次拉取实际使用的车型集合与成员确认状态：裁剪门禁只认快照，
    // 不从别处的异步状态反推（否则旧结果会被拿去裁新作用域）。
    const { key: scopeKey, projectIds, membersResolved } = notifyScope;
    const seq = ++notifyRequestSeq.current;
    setNotifyScopeLoading(true);
    try {
      const results = await Promise.allSettled(projectIds.map((pid) => listRequirements(pid)));
      if (seq !== notifyRequestSeq.current) return; // 已切换山头，丢弃过期结果
      const entries: NotifyEntry<Requirement>[] = [];
      let complete = true;
      results.forEach((res, idx) => {
        if (res.status !== 'fulfilled') {
          complete = false;
          return;
        }
        const pid = projectIds[idx];
        // 跨车型同 reqId 不去重：条目身份是 (projectId, reqId)，各车型各占一条。
        for (const req of res.value.requirements ?? []) {
          entries.push({ projectId: pid, requirement: req });
        }
      });
      setNotifyScopeData({
        scopeKey,
        entries,
        complete,
        membersResolved,
        projectIds,
        requirements: entries.map(({ requirement }) => requirement),
      });
    } finally {
      if (seq === notifyRequestSeq.current) setNotifyScopeLoading(false);
    }
  }, [notifyScope]);

  // 路由 requirements/:projectId 没有 key，切换项目会复用同一组件实例，
  // 必须按作用域变化重读。山头作用域首次读取会顺带合并各车型旧的
  // bsp_notify_list_<projectId>，用户已有列表不会丢。
  useEffect(() => {
    const stored = loadNotifyScope(notifyScope, localStorage);
    setNotifyList(stored);
    setNotifyScopeData(null);
    if (stored.size > 0) void refreshNotifyScope();
  }, [notifyScope, refreshNotifyScope]);

  // 通知列表只保留作用域内真实存在的需求。裁剪的正确性只能由「本次拉取的快照本身」
  // 证明：作用域一致 + 成员已由服务端确认 + 全部请求成功 + 非空 + 车型集合相等。
  // 任一前提不成立一律不裁剪 —— 残留条目最坏是占位行、用户可手动移除，
  // 误裁则会静默丢失兄弟车型加入的共享条目且无法恢复。
  useEffect(() => {
    if (!notifyScopeData) return;
    if (!canPruneNotifyScope(notifyScopeData, notifyScope)) return;
    const next = reconcileNotifyList(
      notifyList,
      notifyScopeData.entries.map(({ projectId, requirement }) => ({
        projectId,
        reqId: requirement.reqId,
      }))
    );
    if (next === notifyList) return;
    setNotifyList(next);
    saveNotifyScope(notifyScope, next, localStorage);
  }, [notifyScope, notifyScopeData, notifyList]);

  /**
   * 加入/移除一条 (车型, 需求)。
   * - 详情卡片只传 reqId，车型取当前页面；
   * - 弹窗移除时传自己的 key，兼容历史遗留的裸 reqId 条目。
   */
  const handleToggleNotifyList = (
    reqId: string,
    projectId: number = numericProjectId,
    explicitKey?: string
  ) => {
    const key = explicitKey ?? scopedNotifyKey(projectId, reqId);
    const adding = !notifyList.has(key);
    setNotifyList((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      saveNotifyScope(notifyScope, next, localStorage);
      return next;
    });
    // 新加入的条目需要解析到所属车型，发送时才能落到正确的 project_id 上。
    if (adding) void refreshNotifyScope();
  };

  // 山头解析结果优先；尚未返回时用当前项目已拉到的需求兜底，避免首帧全是占位行。
  const notifyScopeEntries = useMemo<NotifyEntry<Requirement>[]>(() => {
    if (notifyScopeData && notifyScopeData.scopeKey === notifyScope.key) {
      return notifyScopeData.entries;
    }
    if (allRequirementsProjectId !== numericProjectId) return [];
    return allRequirements.map((requirement) => ({ projectId: numericProjectId, requirement }));
  }, [notifyScopeData, notifyScope.key, allRequirementsProjectId, numericProjectId, allRequirements]);

  // 计数与弹窗行共用这一个派生集合：行数恒等于 notifyList.size（未解析项也出占位行）。
  const notifyRows = useMemo(
    () => buildNotifyRows(notifyList, notifyScopeEntries),
    [notifyList, notifyScopeEntries]
  );
  const unresolvedNotifyCount = useMemo(
    () => notifyRows.reduce((n, row) => (row.requirement ? n : n + 1), 0),
    [notifyRows]
  );

  const projectNameById = useMemo(() => {
    const map = new Map<number, string>();
    for (const p of projects) map.set(p.id, p.name);
    if (project) map.set(project.id, project.name);
    return map;
  }, [projects, project]);

  const handleSendNotification = async () => {
    if (notifyList.size === 0) return;
    if (unresolvedNotifyCount > 0) {
      message.warning(t('通知列表中有无法解析的需求，请先移除后再发送'));
      return;
    }
    setNotifySending(true);
    try {
      // 整批只发一封邮件：条目逐条携带自己的车型，后端合并收件人并按车型分组渲染。
      const items = buildNotifyBatch(notifyRows);
      const password = hiddenPasswordRef.current?.value || '';
      await sendRequirementChangeNotificationBatch(
        items.map(({ projectId, requirement }) => ({
          project_id: projectId,
          req_id: requirement.reqId,
          description: requirement.description,
          owner: requirement.owner,
          delivery_stage: requirement.deliveryStage,
          attachment: requirement.attachment,
          status: requirement.status,
          priority: requirement.priority,
        })),
        project?.mountain?.name ?? null,
        password
      );
      message.success(t('变更通知已发送（{count} 条需求，合并为 1 封邮件）', { count: items.length }));
      setNotifyList(new Set());
      setNotifyScopeData(null);
      setNotifyModalOpen(false);
      // 只清列表本身，保留迁移标记，避免旧项目 key 的数据被重新合并回来。
      clearNotifyScope(notifyScope, localStorage);
    } catch (err: any) {
      const errMsg = err?.response?.data?.error || t('发送失败，请重试');
      message.error(errMsg);
    } finally {
      setNotifySending(false);
    }
  };

  const handleTabChange = (key: string) => {
    setActiveTab(key);
  };

  // Bring a requirement hidden by the filters (or by the active tab) back into view.
  const handleLocateRequirement = (req: Requirement) => {
    if (req.subcategory && req.subcategory !== activeTab) {
      resolvedReqIdRef.current = req.reqId;
      setActiveTab(req.subcategory);
    }
    setFilters(EMPTY_FILTERS);
  };

  // Filtered data
  const filteredRequirements = useMemo(
    () => filterRequirements(requirements, filters),
    [requirements, filters]
  );

  const filteredOverview = useMemo(
    () => (overview ? filterOverview(overview, filters, allRequirements) : null),
    [overview, filters, allRequirements]
  );

  // Data source for filter options: use allRequirements when on overview, otherwise current requirements
  const filterDataSource = activeTab === 'overview' ? allRequirements : requirements;

  if (isSharedRequirements) {
    const baseProjectName = project?.shared_from_project_name || t('基础项目');
    return (
      <div>
        <Alert
          type="info"
          showIcon
          title={
            <span>
              {t('该项目复用「{name}」的需求，请前往', { name: baseProjectName })}
              <Button
                type="link"
                size="small"
                style={{ padding: '0 4px' }}
                onClick={() => navigate(`/requirements/${project!.req_project_id}`)}
              >
                「{baseProjectName}」
              </Button>
              {t('项目查看需求')}
            </span>
          }
        />
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <div className="flex items-center gap-1">
          <Title level={4} style={{ margin: 0 }}>
            {project?.name || t('底软需求')} - {t('需求管理')}
          </Title>
          <CopyExternalLinkButton kind="current" />
        </div>
      </div>
      <SubcategoryTabs
        activeKey={activeTab}
        onChange={handleTabChange}
        newReqSubcats={newReqSubcats}
        canSync={canSync}
        onCloudClick={handleCloudClick}
      />
      <div className="mt-4" style={{ display: 'flex', gap: 16 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {activeTab === 'overview' ? (
            <RequirementOverview
              ref={overviewRef}
              overview={filteredOverview}
              isLoading={isLoading}
              error={error}
              projectId={numericProjectId}
              onReqIdClick={(reqId) => {
                navigate(`?reqId=${encodeURIComponent(reqId)}`);
              }}
            />
          ) : (
            <RequirementList
              requirements={filteredRequirements}
              knownRequirements={allRequirements}
              isLoading={isLoading}
              error={error}
              projectId={numericProjectId}
              projectName={project?.name}
              isMainline={!!project?.is_mainline}
              initialSelectedReqId={targetReqId}
              canEdit={canCreateAndEdit}
              canDelete={canEditRequirements}
              notifyList={notifyList}
              onToggleNotifyList={handleToggleNotifyList}
              onOpenNotifyModal={() => {
                setNotifyModalOpen(true);
                void refreshNotifyScope();
              }}
              onLocateRequirement={handleLocateRequirement}
            />
          )}
        </div>
        <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Button
            icon={<DownloadOutlined />}
            onClick={handleExportClick}
            loading={isExporting}
            disabled={isExporting}
            size="small"
            block
            style={{ width: 220, height: 32 }}
          >
            {isExporting ? t('导出中...') : t('需求导出Excel')}
          </Button>
          <Button
            icon={<ExportOutlined />}
            onClick={() => setFeishuExportOpen(true)}
            size="small"
            block
            style={{ width: 220, height: 32 }}
            disabled={!canEditRequirements}
          >
            {t('需求导出飞书')}
          </Button>
          <Button
            icon={<SyncOutlined />}
            onClick={() => setFeishuSyncOpen(true)}
            size="small"
            block
            style={{ width: 220, height: 32 }}
            disabled={!canEditRequirements}
          >
            {t('从飞书回流需求')}
          </Button>
          {activeTab !== 'overview' && (
            <Button
              icon={<PlusOutlined />}
              onClick={() => setCreateModalOpen(true)}
              size="small"
              block
              style={{ width: 220, height: 32 }}
              disabled={!canCreateAndEdit}
            >
              {t('新增需求')}
            </Button>
          )}
          {canUseSharedNotifyList({
            userType: user?.user_type,
            reqMode: project?.req_mode,
          }) && (
            <Button
              icon={<NotificationOutlined />}
              onClick={() => {
                setNotifyModalOpen(true);
                void refreshNotifyScope();
              }}
              size="small"
              block
              style={{ width: 220, height: 32 }}
            >
              {t('通知列表({count})', { count: notifyList.size })}
            </Button>
          )}
          <RequirementFilter
            filters={filters}
            onChange={setFilters}
            requirements={filterDataSource}
          />
          {activeTab === 'overview' && user?.user_type === 'internal' && (
            <Space orientation="vertical" style={{ width: '100%', marginTop: 16 }}>
              <Button
                icon={<EyeOutlined />}
                onClick={handlePreviewClick}
                size="small"
                block
                style={{ width: 220, height: 32 }}
              >
                {t('版本变更查看')}
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handlePublishClick}
                size="small"
                block
                style={{ width: 220, height: 32 }}
              >
                {t('创建版本标签')}
              </Button>
            </Space>
          )}
        </div>
      </div>
      <RequirementCreateModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        projectId={numericProjectId}
        subcategory={activeTab}
        isMainlinePool={isMainlinePool}
        otherMainlineProjects={otherMainlineProjects}
        onSuccess={() => {
          fetchRequirements(numericProjectId, activeTab);
          fetchAllRequirements(numericProjectId);
        }}
      />
      <PublishVersionModal
        open={publishModalOpen}
        projectId={numericProjectId}
        existingVersionNumbers={[]}
        onSuccess={handlePublishClose}
        onClose={handlePublishClose}
      />
      <PreviewChangesModal open={previewModalOpen} projectId={numericProjectId} onClose={handlePreviewClose} />
      <FeishuExportModal
        open={feishuExportOpen}
        onClose={() => setFeishuExportOpen(false)}
        projectId={numericProjectId}
        projectName={project?.name || ''}
        onSuccess={() => fetchAllRequirements(numericProjectId)}
      />
      <FeishuSyncModal
        open={feishuSyncOpen}
        onClose={() => setFeishuSyncOpen(false)}
        projectId={numericProjectId}
        projectName={project?.name || ''}
        onSuccess={() => { fetchAllRequirements(numericProjectId); fetchOverview(numericProjectId); }}
      />
      <Modal
        title={t('需求变更通知')}
        open={notifyModalOpen}
        onCancel={() => setNotifyModalOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setNotifyModalOpen(false)}>
            {t('取消')}
          </Button>,
          <Button
            key="send"
            type="primary"
            icon={<NotificationOutlined />}
            loading={notifySending}
            disabled={notifyRows.length === 0 || unresolvedNotifyCount > 0}
            onClick={handleSendNotification}
          >
            {t('发送变更通知')}
          </Button>,
        ]}
      >
        <p style={{ marginBottom: 12, color: '#666' }}>
          {t('项目：')}{project?.name || '—'}
          {mountainId != null && (
            <span>
              {' '}· {t('山头「{name}」下', { name: project?.mountain?.name || String(mountainId ?? '') })}{' '}
              {t('{count} 个车型共用此列表', { count: notifyScope.projectIds.length })}
            </span>
          )}
        </p>
        {/* 视觉隐藏但可被浏览器自动填充：不能用 display:none，否则密码管理器不填。
            必须包在独立 <form> 里并带上 username 字段，否则 Chrome 会把整页当成登录表单，
            把保存的用户名自动填进侧栏「内容」过滤框。 */}
        <form
          autoComplete="on"
          onSubmit={(e) => e.preventDefault()}
          aria-hidden="true"
          style={{
            position: 'absolute',
            left: '-9999px',
            top: 0,
            width: 1,
            height: 1,
            overflow: 'hidden',
          }}
        >
          <input
            type="text"
            name="username"
            autoComplete="username"
            defaultValue={user?.username ?? ''}
            aria-hidden="true"
            tabIndex={-1}
            style={{ width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}
          />
          <input
            type="password"
            ref={hiddenPasswordRef}
            autoComplete="current-password"
            name="password"
            aria-hidden="true"
            tabIndex={-1}
            style={{ width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}
          />
        </form>
        {unresolvedNotifyCount > 0 && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            title={t('有 {count} 条需求在当前山头内无法解析（无权访问所属车型或需求已删除），请先移除后再发送', { count: unresolvedNotifyCount })}
          />
        )}
        {notifyRows.length === 0 ? (
          <p style={{ color: '#999' }}>
            {t('通知列表为空。可在同一山头任一车型的需求里，点击需求详情卡片的「通知列表」加入（本列表由山头内各车型共用）')}
          </p>
        ) : (
          <List
            size="small"
            loading={notifyScopeLoading}
            dataSource={notifyRows}
            rowKey="key"
            renderItem={(row) => {
              const req = row.requirement;
              // 每条只属于一个车型：跨车型同 reqId 是两行，且各自显示自己的车型。
              const modelName = projectNameById.get(row.projectId)
                ?? (row.projectId ? `#${row.projectId}` : null);
              return (
                <List.Item
                  actions={[
                    <Button
                      key="remove"
                      type="link"
                      danger
                      size="small"
                      onClick={() => handleToggleNotifyList(row.reqId, row.projectId, row.key)}
                    >
                      {t('移除')}
                    </Button>,
                  ]}
                >
                  <List.Item.Meta
                    title={
                      modelName ? (
                        <span>
                          {row.reqId}
                          <span style={{ marginLeft: 8, color: '#999', fontWeight: 'normal' }}>
                            {modelName}
                          </span>
                        </span>
                      ) : (
                        row.reqId
                      )
                    }
                    description={
                      req == null
                        ? t('无法解析：无权访问所属车型或需求已删除')
                        : req.description
                          ? req.description.length > 60
                            ? req.description.slice(0, 60) + '...'
                            : req.description
                          : t('无描述')
                    }
                  />
                </List.Item>
              );
            }}
          />
        )}
      </Modal>
    </div>
  );
};

export default RequirementManagement;
